<template>
  <modal
    header
    :footer="true"
    disable-click-outside
    class="foxforesight-modal"
    data-test="graphics-foxforesightConnectModal"
    auto-height
    @close="onCancel"
  >
    <!-- Header -->
    <template #modal-header>
      <h2>Setup Fox Foresight Chart</h2>
      <button
        class="modal-close"
        aria-label="Close modal"
        data-test="graphics-foxforesightConnectModal-close"
        @click="onCancel"
      >
        <span class="fa fa-times" />
      </button>
    </template>

    <!-- Body -->
    <template #modal-body>
      <div class="foxforesight-modal__description">
        <label>
          Chart Type
          <select
            v-model="chartType"
            class="width-full"
            data-test="graphics-foxforesightModal-chartType"
            data-entity="graphics-foxforesightModal-chartType"
            @change="onChartTypeChange"
          >
            <option value="">
              Select a chart type
            </option>
            <option
              v-for="chart in availableCharts"
              :key="chart.value"
              :value="chart.value"
            >
              {{ chart.label }}
            </option>
          </select>
        </label>
      </div>

      <div
        v-if="chartType"
        class="foxforesight-modal__description"
      >
        <label>
          Date
          <div class="datepicker">
            <date-picker
              v-model:value="selectedDate"
              type="date"
              format="YYYY-MM-DD"
              value-type="format"
              :clearable="false"
              placeholder="Select date"
              data-test="graphics-foxforesightModal-date"
              data-entity="graphics-foxforesightModal-date"
              @change="onDateChange"
            />
          </div>
        </label>
      </div>

      <div
        v-if="chartType && selectedDate"
        class="foxforesight-modal__description"
      >
        <label>
          Game
          <select
            v-model="selectedGame"
            class="width-full"
            data-test="graphics-foxforesightModal-game"
            data-entity="graphics-foxforesightModal-game"
            :disabled="loadingGames"
            @change="onGameChange"
          >
            <option value="">
              {{ loadingGames ? 'Loading games...' : 'Select a game' }}
            </option>
            <option
              v-for="game in availableGames"
              :key="game.id"
              :value="game.id"
            >
              {{ game.name }}
            </option>
          </select>
        </label>
      </div>

      <!-- Dynamic parameters based on chart type and game -->
      <template v-if="chartParameters.length > 0">
        <div
          v-for="param in chartParameters"
          :key="param.key"
          class="foxforesight-modal__description"
        >
          <label>
            {{ param.label }}

            <!-- Single select (treating multi-select as single for API compatibility) -->
            <select
              v-if="param.type === 'select' || param.type === 'multi-select'"
              v-model="parameterValues[param.key]"
              class="width-full"
              :data-test="`graphics-foxforesightModal-${param.key}`"
              :data-entity="`graphics-foxforesightModal-${param.key}`"
            >
              <option value="">
                Select...
              </option>
              <option
                v-for="option in param.options"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>

            <!-- Text input -->
            <input
              v-else
              v-model="parameterValues[param.key]"
              type="text"
              class="width-full"
              :placeholder="param.label"
              :data-test="`graphics-foxforesightModal-${param.key}`"
              :data-entity="`graphics-foxforesightModal-${param.key}`"
            >
          </label>
        </div>
      </template>
    </template>

    <!-- Footer -->
    <template #modal-footer>
      <div class="display-flex items-center gap-1 flex-end">
        <button
          class="btn btn-style-b"
          data-test="graphics-foxforesightModal-cancel"
          @click="onCancel"
        >
          Cancel
        </button>
        <button
          class="btn btn-style-a no-tooltip"
          :disabled="!isValid"
          data-test="graphics-foxforesightModal-connect"
          data-entity="graphics-foxforesightModal-connect"
          @click="onConnect"
        >
          Add Chart
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import datePicker from 'vue-datepicker-next';
import Modal from './Modal.vue';
import Partners from '../../services/partners';

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default {
  emits: ['close', 'connect'],
  name: 'FoxForesightConnectModal',

  components: {
    Modal,
    datePicker,
  },

  data() {
    return {
      chartType: '',
      selectedDate: getTodayDate(),
      selectedGame: '',
      availableCharts: [],
      availableGames: [],
      loadingGames: false,
      loadingParameters: false,
      chartParameters: [],
      parameterValues: {},
      selectedChartSport: '',
    };
  },

  computed: {
    isValid() {
      // Only Chart Type, Date, and Game are required
      // All parameters after Game are optional
      return !!(this.chartType && this.selectedDate && this.selectedGame);
    },
  },

  async mounted() {
    await this.loadCharts();
    // Load initial games for today's date
    if (this.selectedDate) {
      await this.loadGames();
    }
  },

  methods: {
    async loadCharts() {
      try {
        const charts = await Partners.getFoxForesightCharts();
        // API returns array directly, map to format used by dropdown
        this.availableCharts = (charts || []).map((chart) => ({
          value: chart.chart_type,
          label: chart.label,
          sport: chart.sport,
        }));
      } catch (error) {
        console.error('Failed to load charts:', error);
      }
    },

    async loadGames() {
      if (!this.selectedDate) return;

      this.loadingGames = true;
      try {
        const games = await Partners.getFoxForesightGames(this.selectedDate, this.selectedChartSport);
        // API returns array directly, map to format used by dropdown
        this.availableGames = (games || []).map((game) => ({
          id: game.game_id,
          name: game.label,
        }));
      } catch (error) {
        console.error('Failed to load games:', error);
      } finally {
        this.loadingGames = false;
      }
    },

    async loadChartParameters() {
      if (!this.chartType || !this.selectedGame) return;

      this.loadingParameters = true;
      try {
        const data = await Partners.getFoxForesightChartOptions(this.chartType, this.selectedGame);
        this.chartParameters = data.parameters || [];

        // Initialize parameter values with defaults
        const newValues = {};
        this.chartParameters.forEach((param) => {
          if (param.type === 'select' || param.type === 'multi-select') {
            // For player_id, hit_result, hit_direction, and pitcher_hand,
            // always default to empty/"All" option
            const alwaysEmptyFields = ['player_id', 'hit_result', 'hit_direction', 'pitcher_hand'];
            if (alwaysEmptyFields.includes(param.key)) {
              newValues[param.key] = '';
            } else {
              // For other fields (like view), prefer first option
              const emptyOption = param.options.find((opt) => opt.value === '');
              newValues[param.key] = emptyOption ? '' : (param.options?.[0]?.value ?? '');
            }
          } else {
            newValues[param.key] = '';
          }
        });
        this.parameterValues = newValues;
      } catch (error) {
        console.error('Failed to load chart parameters:', error);
        this.chartParameters = [];
      } finally {
        this.loadingParameters = false;
      }
    },

    onChartTypeChange() {
      // Find the selected chart and get its sport
      const selectedChart = this.availableCharts.find((chart) => chart.value === this.chartType);
      this.selectedChartSport = selectedChart?.sport || '';

      this.selectedGame = '';
      this.availableGames = [];
      this.chartParameters = [];
      this.parameterValues = {};
      // Don't reset date - keep the current date
      // Load games if date is already set
      if (this.selectedDate) {
        this.loadGames();
      }
    },

    onDateChange() {
      this.selectedGame = '';
      this.availableGames = [];
      this.chartParameters = [];
      this.parameterValues = {};
      this.loadGames();
    },

    onGameChange() {
      this.chartParameters = [];
      this.parameterValues = {};
      if (this.selectedGame) {
        this.loadChartParameters();
      }
    },

    onCancel() {
      this.$emit('close');
    },

    onConnect() {
      if (!this.isValid) {
        return;
      }

      const config = {
        chartType: this.chartType,
        date: this.selectedDate,
        game: this.selectedGame,
      };

      // Add all parameter values to config
      this.chartParameters.forEach((param) => {
        const value = this.parameterValues[param.key];
        if (value !== '' && value !== null && value !== undefined) {
          config[param.key] = value;
        }
      });

      this.$emit('connect', config);
      this.onCancel();
    },
  },
};
</script>

<style lang="scss" scoped>
.foxforesight-modal {
  &__description {
    margin-bottom: 1rem;

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 600;
    }

    input,
    select {
      display: block;
      width: 100%;
      margin-top: 0.5rem;
    }

    .datepicker {
      margin-top: 0.5rem;
    }
  }

  .text-muted {
    font-size: 0.875rem;
    color: #6c757d;
  }
}
</style>
